import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Card,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Typography,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  CreditCardOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../../contexts/CompanyContext';
import { useSecureRequest } from '../../../hooks/useSecureRequest';
import {
  useTreasuryAccounts,
  useContacts,
  mutateTreasuryAccounts,
  mutateAccounts,
  mutateContacts,
} from '../../../hooks/useAccounting';
import {
  TreasuryAccountItem,
  TreasuryType,
  isPartnerContact,
} from '../../../services/accountingService';
import { UnifiedContactModal } from '@ryo/ui';
import { formatCurrency } from '../journal-entries/journalEntriesHelpers';

const { Option } = Select;
const { Text } = Typography;

const TYPE_CONFIG: Record<
  TreasuryType,
  { label: string; color: string; icon: React.ReactNode }
> = {
  CASH: { label: 'Caja / Efectivo', color: 'green', icon: <DollarCircleOutlined /> },
  BANK: { label: 'Banco (Ahorros / Corriente)', color: 'blue', icon: <BankOutlined /> },
  WALLET: { label: 'Billetera Digital', color: 'purple', icon: <WalletOutlined /> },
  CREDIT_CARD: { label: 'Tarjeta de Crédito Corporativa', color: 'orange', icon: <CreditCardOutlined /> },
  PARTNER: { label: 'Cuenta Personal de Socio', color: 'cyan', icon: <UserOutlined /> },
};

export const TreasuryAccountsTab: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading: actionLoading } = useSecureRequest();
  const [form] = Form.useForm();

  const { accounts, isLoading, mutate } = useTreasuryAccounts({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
  });

  const { contacts } = useContacts({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<TreasuryAccountItem | null>(null);
  const [selectedType, setSelectedType] = useState<TreasuryType>('BANK');


  const [partnerModalOpen, setPartnerModalOpen] = useState(false);

  // Configured partner contact IDs in treasury accounts
  const configuredPartnerContactIds = useMemo(() => {
    return new Set(
      accounts.filter((a) => a.type === 'PARTNER' && a.contact_id).map((a) => a.contact_id as number)
    );
  }, [accounts]);

  // Only contacts with a valid, non-empty document_number AND have at least the PARTNER role or are already configured
  const validPartnerContacts = useMemo(() => {
    const seen = new Set();
    return contacts.filter((c) => {
      if (!c?.id) return false;
      const doc = String(c.document_number || '').trim();
      if (!doc) return false;
      const isPartner = isPartnerContact(c, configuredPartnerContactIds);
      if (!isPartner) return false;
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [contacts, configuredPartnerContactIds]);

  const handleSavePartnerContact = async (values: any) => {
    const dataRequest: any = {
      company: company.current?.uid,
      company_id: company.current?.id,
      module: 'ACCOUNTING',
      contact: {
        ...values,
        company_id: company.current?.id,
        roles: [{ role_type: 'PARTNER' }],
      },
    };
    const res = await securePost('/user/contact/create-contact', dataRequest);
    if (res?.status && res.status !== 200 && res.status !== 201) {
      throw new Error(res?.message || 'Error al guardar el socio');
    }
    message.success('Socio creado exitosamente');
    await mutateContacts(company.current?.id);
    const newContact = res?.contact || res;
    if (newContact?.id) {
      form.setFieldsValue({ contact_id: newContact.id });
    }
    return newContact;
  };

  // KPI calculations
  const totalCash = useMemo(
    () => accounts.filter((a) => a.type === 'CASH').reduce((s, a) => s + (Number(a.current_balance) || 0), 0),
    [accounts]
  );
  const totalBank = useMemo(
    () => accounts.filter((a) => a.type === 'BANK').reduce((s, a) => s + (Number(a.current_balance) || 0), 0),
    [accounts]
  );
  const totalWallet = useMemo(
    () => accounts.filter((a) => a.type === 'WALLET').reduce((s, a) => s + (Number(a.current_balance) || 0), 0),
    [accounts]
  );
  const partnerAccountsCount = useMemo(
    () => accounts.filter((a) => a.type === 'PARTNER').length,
    [accounts]
  );

  const handleOpenModal = (acc?: TreasuryAccountItem) => {
    if (acc) {
      setEditingAccount(acc);
      setSelectedType(acc.type);
      form.setFieldsValue({
        name: acc.name,
        type: acc.type,
        bank_name: acc.bank_name,
        account_number: acc.account_number,
        contact_id: acc.contact_id || undefined,
        allow_inflows: acc.allow_inflows !== false,
        allow_outflows: acc.allow_outflows !== false,
        is_default_cash: Boolean(acc.is_default_cash),
        is_default_bank: Boolean(acc.is_default_bank),
      });
    } else {
      setEditingAccount(null);
      setSelectedType('BANK');
      form.setFieldsValue({
        name: '',
        type: 'BANK',
        bank_name: '',
        account_number: '',
        contact_id: undefined,
        allow_inflows: true,
        allow_outflows: true,
        is_default_cash: false,
        is_default_bank: false,
      });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        company_id: company.current?.id,
        company: company.current?.uid || company.current?.id,
        id: editingAccount?.id,
        ...values,
      };

      const res = await securePost('/accounting/treasury-accounts/save-account', payload);

      if (res?.data || res?.success || res?.status === 200) {
        message.success(
          editingAccount ? 'Cuenta de tesorería actualizada' : 'Cuenta de tesorería creada exitosamente'
        );
        setModalVisible(false);
        form.resetFields();
        void mutate();
        void mutateTreasuryAccounts(company.current?.id);
        void mutateAccounts(company.current?.id);
      } else {
        message.error(res?.message || 'Error al guardar la cuenta');
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      console.error('Error saving treasury account:', err);
      message.error(err?.message || 'Error al guardar la cuenta');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await securePost('/accounting/treasury-accounts/delete-account', {
        company_id: company.current?.id,
        company: company.current?.uid || company.current?.id,
        id,
      });

      if (res?.success || res?.status === 200) {
        message.success('Cuenta eliminada exitosamente');
        void mutate();
        void mutateTreasuryAccounts(company.current?.id);
      } else {
        message.error(res?.message || 'Error al eliminar la cuenta');
      }
    } catch (err: any) {
      console.error('Error deleting treasury account:', err);
      message.error(err?.message || 'Error al eliminar la cuenta');
    }
  };

  const columns = [
    {
      title: 'Nombre de Cuenta',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: TreasuryAccountItem) => {
        const conf = TYPE_CONFIG[r.type] || TYPE_CONFIG.CASH;
        return (
          <Space>
            <span style={{ fontSize: 16 }}>{conf.icon}</span>
            <div>
              <Text strong>{name}</Text>
              {r.is_default_cash && (
                <Tag color="green" style={{ marginLeft: 6, fontSize: 10 }}>
                  Caja Defecto
                </Tag>
              )}
              {r.is_default_bank && (
                <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>
                  Banco Defecto
                </Tag>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: TreasuryType) => {
        const conf = TYPE_CONFIG[type] || TYPE_CONFIG.CASH;
        return <Tag color={conf.color}>{conf.label}</Tag>;
      },
    },
    {
      title: 'Entidad / No. Cuenta',
      key: 'bank_details',
      render: (_: any, r: TreasuryAccountItem) => {
        if (r.type === 'PARTNER') {
          return (
            <Space size={4}>
              <UserOutlined style={{ color: '#13c2c2' }} />
              <span>{r.contact?.name || 'Socio vinculado'}</span>
            </Space>
          );
        }
        if (!r.bank_name && !r.account_number) return <Text type="secondary">-</Text>;
        return (
          <div>
            <div>{r.bank_name || '-'}</div>
            {r.account_number && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                No: {r.account_number}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Subcuenta PUC',
      key: 'puc_account',
      render: (_: any, r: TreasuryAccountItem) => {
        if (!r.puc_account) return <Text type="secondary">Automática según tipo</Text>;
        return (
          <Space size={4}>
            <Tag color="geekblue">{r.puc_account.code}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.puc_account.name}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Permisos Flujo',
      key: 'flow',
      width: 160,
      render: (_: any, r: TreasuryAccountItem) => (
        <Space size={4}>
          <Tag color={r.allow_inflows !== false ? 'success' : 'default'}>
            {r.allow_inflows !== false ? 'Cobros OK' : 'Sin Cobros'}
          </Tag>
          <Tag color={r.allow_outflows !== false ? 'processing' : 'default'}>
            {r.allow_outflows !== false ? 'Pagos OK' : 'Sin Pagos'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Saldo en Libros',
      dataIndex: 'current_balance',
      key: 'current_balance',
      align: 'right' as const,
      width: 160,
      render: (val: number, r: TreasuryAccountItem) => {
        if (r.type === 'PARTNER') {
          return <Text type="secondary">Ver en pestaña Socios</Text>;
        }
        const isNeg = (val || 0) < 0;
        return (
          <Text strong style={{ color: isNeg ? '#f5222d' : '#52c41a', fontSize: 14 }}>
            {formatCurrency(val || 0)}
          </Text>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center' as const,
      width: 100,
      render: (_: any, r: TreasuryAccountItem) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(r)}
          />
          <Popconfirm
            title="¿Eliminar cuenta de tesorería?"
            description="Solo se permite si la cuenta no tiene movimientos contables registrados."
            onConfirm={() => r.id && handleDelete(r.id)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ background: '#f6ffed' }}>
            <Statistic
              title="Disponible en Cajas"
              value={totalCash}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#52c41a' }}
              prefix={<DollarCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ background: '#e6f7ff' }}>
            <Statistic
              title="Disponible en Bancos"
              value={totalBank}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#1890ff' }}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ background: '#f9f0ff' }}>
            <Statistic
              title="Billeteras Digitales"
              value={totalWallet}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#722ed1' }}
              prefix={<WalletOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ background: '#e6fffb' }}>
            <Statistic
              title="Cuentas de Socios"
              value={partnerAccountsCount}
              valueStyle={{ color: '#13c2c2' }}
              prefix={<UserOutlined />}
              suffix="configuradas"
            />
          </Card>
        </Col>
      </Row>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Cuentas Bancarias, Cajas y Cuentas de Socios
          </Text>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            Estas cuentas están disponibles para seleccionar el origen o destino de fondos en Compras, Ventas, Gastos y Tesorería.
          </div>
        </div>
        <Space>
          <Button
            icon={<UserOutlined />}
            onClick={() => setPartnerModalOpen(true)}
          >
            Nuevo Socio
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            Nueva Cuenta
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={accounts}
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        size="middle"
      />

      {/* Modal Nueva / Editar Cuenta */}
      <Modal
        title={
          <Space>
            <BankOutlined style={{ color: '#1890ff' }} />
            <span>{editingAccount ? 'Editar Cuenta de Tesorería' : 'Nueva Cuenta de Tesorería'}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={actionLoading}
        okText={editingAccount ? 'Guardar Cambios' : 'Crear Cuenta'}
        cancelText="Cancelar"
        width={560}
      >
        <Alert
          message="Integración Contable Automática en el PUC"
          description="Al crear una nueva cuenta bancaria o billetera (ej. Bancolombia o Nequi), el sistema genera automáticamente su subcuenta hija en el PUC (111005xx) para ver los saldos individuales en el Balance de Comprobación."
          type="info"
          showIcon
          style={{ marginBottom: 16, fontSize: 12 }}
        />

        <Form form={form} layout="vertical">
          <Form.Item
            label="Tipo de Cuenta"
            name="type"
            rules={[{ required: true, message: 'Seleccione el tipo de cuenta' }]}
          >
            <Select
              onChange={(val) => {
                setSelectedType(val);
                if (val === 'CASH') {
                  form.setFieldsValue({ bank_name: '', account_number: '', contact_id: undefined });
                }
              }}
            >
              <Option value="BANK">Banco (Cuenta de Ahorros / Corriente)</Option>
              <Option value="WALLET">Billetera Digital (Nequi, Daviplata, etc.)</Option>
              <Option value="CASH">Caja / Efectivo</Option>
              <Option value="CREDIT_CARD">Tarjeta de Crédito Corporativa</Option>
              <Option value="PARTNER">Cuenta de Socio / Accionista</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Nombre o Alias de la Cuenta"
            name="name"
            rules={[{ required: true, message: 'Ingrese un nombre identificable' }]}
          >
            <Input placeholder="Ej: Bancolombia Principal Ahorros / Nequi Empresa / Caja Menor Tienda 1" />
          </Form.Item>

          {selectedType === 'PARTNER' && (
            <Form.Item
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>Socio / Accionista Titular</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, height: 'auto', fontSize: 12, marginLeft: 16 }}
                    onClick={() => setPartnerModalOpen(true)}
                  >
                    + Nuevo Socio
                  </Button>
                </div>
              }
              name="contact_id"
              rules={[{ required: true, message: 'Seleccione el socio vinculado a esta cuenta' }]}
              tooltip="Solo se muestran contactos con rol PARTNER (Socio / Accionista) y Cédula/NIT registrado"
              extra={
                validPartnerContacts.length === 0
                  ? 'No hay contactos con rol de Socio y documento registrado. Puede crearlo con el botón "+ Nuevo Socio".'
                  : undefined
              }
            >
              <Select
                showSearch
                placeholder="Buscar socio por nombre o documento..."
                optionFilterProp="children"
              >
                {validPartnerContacts.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name} {c.document_number ? `(${c.document_number})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {(selectedType === 'BANK' || selectedType === 'WALLET' || selectedType === 'CREDIT_CARD') && (
            <>
              <Form.Item label="Entidad Financiera / Banco" name="bank_name">
                <Input placeholder="Ej: Bancolombia / Davivienda / BBVA / Nequi" />
              </Form.Item>

              <Form.Item label="Número de Cuenta (o últimos 4 dígitos)" name="account_number">
                <Input placeholder="Ej: 4920 / 031-829103-91" />
              </Form.Item>
            </>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Habilitar para Cobros (Entradas)"
                name="allow_inflows"
                valuePropName="checked"
              >
                <Switch defaultChecked />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Habilitar para Pagos (Salidas)"
                name="allow_outflows"
                valuePropName="checked"
              >
                <Switch defaultChecked />
              </Form.Item>
            </Col>
          </Row>

          {selectedType === 'CASH' && (
            <Form.Item
              label="¿Es la Caja Predeterminada de la Empresa?"
              name="is_default_cash"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}

          {selectedType === 'BANK' && (
            <Form.Item
              label="¿Es la Cuenta Bancaria Predeterminada de la Empresa?"
              name="is_default_bank"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <UnifiedContactModal
        visible={partnerModalOpen}
        onCancel={() => setPartnerModalOpen(false)}
        onSuccess={(newContact) => {
          setPartnerModalOpen(false);
          if (newContact?.id) {
            form.setFieldsValue({ contact_id: newContact.id });
          }
        }}
        onSave={handleSavePartnerContact}
        role="PARTNER"
        validationMode="strict"
      />
    </div>
  );
};

export default TreasuryAccountsTab;